import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare, Send, AlertTriangle, Clock, User, Briefcase, Calendar,
  CheckCircle2, XCircle, AlertCircle, FileText, Upload, X, Eye, EyeOff,
  RotateCcw, ArrowLeft, Tag, MessageCircle
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { toast } from 'sonner';
import { format, formatDistanceToNow, differenceInHours, differenceInMinutes } from 'date-fns';
import { ar } from 'date-fns/locale';

const TicketDetailsPage = () => {
  const { ticketId } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [addToTimeline, setAddToTimeline] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [lawyers, setLawyers] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchTicketDetails();
    fetchLawyers();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const [ticketRes, repliesRes] = await Promise.all([
        api.get(`/tickets-v2/${ticketId}`),
        api.get(`/tickets-v2/replies/${ticketId}`)
      ]);
      setTicket(ticketRes.data);
      setReplies(repliesRes.data);
      
      // Build timeline from ticket and replies
      buildTimeline(ticketRes.data, repliesRes.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل التذكرة' : 'Failed to load ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchLawyers = async () => {
    try {
      const response = await api.get('/users?role=lawyer');
      setLawyers(response.data);
    } catch (error) {
      console.error('Failed to load lawyers:', error);
    }
  };

  const buildTimeline = (ticketData, repliesData) => {
    const events = [];
    
    // Ticket creation
    events.push({
      id: 'created',
      type: 'created',
      title: i18n.language === 'ar' ? 'تم إنشاء التذكرة' : 'Ticket Created',
      description: `${ticketData.ticket_number} - ${ticketData.title}`,
      user: ticketData.client_name,
      timestamp: ticketData.created_at,
      icon: MessageSquare,
      color: 'text-blue-600'
    });
    
    // Status changes (simulated from ticket data)
    if (ticketData.assigned_to) {
      events.push({
        id: 'assigned',
        type: 'assigned',
        title: i18n.language === 'ar' ? 'تم الإسناد' : 'Assigned',
        description: i18n.language === 'ar' 
          ? `تم إسناد التذكرة إلى ${ticketData.assigned_to_name}`
          : `Ticket assigned to ${ticketData.assigned_to_name}`,
        user: 'System',
        timestamp: ticketData.updated_at,
        icon: User,
        color: 'text-purple-600'
      });
    }
    
    // Replies
    repliesData.forEach((reply, index) => {
      events.push({
        id: `reply-${reply.id}`,
        type: reply.is_internal_note ? 'internal_note' : 'reply',
        title: reply.is_internal_note 
          ? (i18n.language === 'ar' ? 'ملاحظة داخلية' : 'Internal Note')
          : (i18n.language === 'ar' ? 'رد جديد' : 'New Reply'),
        description: reply.message,
        user: reply.user_name,
        role: reply.user_role,
        timestamp: reply.created_at,
        icon: reply.is_internal_note ? Eye : MessageCircle,
        color: reply.is_internal_note ? 'text-yellow-600' : 'text-green-600'
      });
    });
    
    // Check for SLA breach
    if (ticketData.is_overdue) {
      events.push({
        id: 'sla-breach',
        type: 'sla_breach',
        title: i18n.language === 'ar' ? 'تجاوز SLA' : 'SLA Breach',
        description: i18n.language === 'ar' ? 'تم تجاوز وقت الاستجابة المحدد' : 'Response time exceeded',
        user: 'System',
        timestamp: ticketData.sla_due_time,
        icon: AlertTriangle,
        color: 'text-red-600'
      });
    }
    
    // Ticket closure
    if (ticketData.status === 'closed' && ticketData.closed_at) {
      events.push({
        id: 'closed',
        type: 'closed',
        title: i18n.language === 'ar' ? 'تم إغلاق التذكرة' : 'Ticket Closed',
        description: i18n.language === 'ar' ? 'تم حل المشكلة وإغلاق التذكرة' : 'Issue resolved and ticket closed',
        user: 'System',
        timestamp: ticketData.closed_at,
        icon: CheckCircle2,
        color: 'text-green-600'
      });
    }
    
    // Sort by timestamp (newest first)
    events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setTimeline(events);
  };

  const getStatusBadge = (status) => {
    const configs = {
      new: { 
        label: i18n.language === 'ar' ? 'جديدة' : 'New',
        className: 'bg-blue-500 text-white',
        icon: AlertCircle
      },
      in_progress: { 
        label: i18n.language === 'ar' ? 'قيد المعالجة' : 'In Progress',
        className: 'bg-yellow-500 text-white',
        icon: Clock
      },
      replied: { 
        label: i18n.language === 'ar' ? 'تم الرد' : 'Replied',
        className: 'bg-green-500 text-white',
        icon: CheckCircle2
      },
      closed: { 
        label: i18n.language === 'ar' ? 'مغلقة' : 'Closed',
        className: 'bg-gray-500 text-white',
        icon: XCircle
      },
      reopened: { 
        label: i18n.language === 'ar' ? 'معاد فتحها' : 'Reopened',
        className: 'bg-purple-500 text-white',
        icon: RotateCcw
      }
    };
    
    const config = configs[status] || configs.new;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.className} gap-1.5 text-sm px-3 py-1`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const configs = {
      urgent: { label: i18n.language === 'ar' ? 'عاجل' : 'Urgent', className: 'bg-red-600 text-white' },
      high: { label: i18n.language === 'ar' ? 'عالي' : 'High', className: 'bg-orange-500 text-white' },
      normal: { label: i18n.language === 'ar' ? 'عادي' : 'Normal', className: 'bg-blue-500 text-white' },
      low: { label: i18n.language === 'ar' ? 'منخفض' : 'Low', className: 'bg-gray-500 text-white' }
    };
    const config = configs[priority] || configs.normal;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getSLATimer = () => {
    if (!ticket.sla_due_time || ticket.status === 'closed') return null;
    
    const now = new Date();
    const dueTime = new Date(ticket.sla_due_time);
    const hoursRemaining = differenceInHours(dueTime, now);
    const minutesRemaining = differenceInMinutes(dueTime, now) % 60;
    
    if (ticket.is_overdue) {
      const hoursOverdue = Math.abs(hoursRemaining);
      return (
        <div className="flex items-center gap-2 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
          <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center animate-pulse">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-red-800 font-semibold">
              {i18n.language === 'ar' ? 'متأخرة!' : 'Overdue!'}
            </p>
            <p className="text-lg font-bold text-red-600">
              {i18n.language === 'ar' 
                ? `${hoursOverdue} ساعة تأخير`
                : `${hoursOverdue}h overdue`
              }
            </p>
          </div>
        </div>
      );
    } else if (hoursRemaining <= 2) {
      return (
        <div className="flex items-center gap-2 p-4 bg-orange-50 border-2 border-orange-200 rounded-xl">
          <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
            <Clock className="w-6 h-6 text-white animate-pulse" />
          </div>
          <div>
            <p className="text-sm text-orange-800 font-semibold">
              {i18n.language === 'ar' ? 'وقت محدود!' : 'Time Critical!'}
            </p>
            <p className="text-lg font-bold text-orange-600">
              {hoursRemaining > 0 
                ? `${hoursRemaining}h ${minutesRemaining}m ${i18n.language === 'ar' ? 'متبقي' : 'remaining'}`
                : `${minutesRemaining}m ${i18n.language === 'ar' ? 'متبقي' : 'remaining'}`
              }
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-green-800 font-semibold">
              {i18n.language === 'ar' ? 'في الوقت المحدد' : 'On Time'}
            </p>
            <p className="text-lg font-bold text-green-600">
              {hoursRemaining}h {i18n.language === 'ar' ? 'متبقي' : 'remaining'}
            </p>
          </div>
        </div>
      );
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!replyMessage.trim()) {
      toast.error(i18n.language === 'ar' ? 'يرجى كتابة رسالة' : 'Please write a message');
      return;
    }
    
    setSendingReply(true);
    try {
      await api.post('/tickets-v2/replies', {
        ticket_id: ticketId,
        message: replyMessage,
        is_internal_note: isInternalNote,
        attachments: []
      });
      
      // If addToTimeline is checked and it's staff reply, create timeline event
      if (addToTimeline && user.role !== 'client') {
        // This would typically be handled by the backend
        // For now, we'll just refetch the data
      }
      
      toast.success(i18n.language === 'ar' ? 'تم إرسال الرد بنجاح' : 'Reply sent successfully');
      setReplyMessage('');
      setIsInternalNote(false);
      setAddToTimeline(false);
      fetchTicketDetails();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل في إرسال الرد' : 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await api.put(`/tickets-v2/${ticketId}`, { status: newStatus });
      toast.success(i18n.language === 'ar' ? 'تم تحديث الحالة' : 'Status updated');
      fetchTicketDetails();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحديث الحالة' : 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAssignTicket = async (lawyerId) => {
    try {
      await api.post('/tickets-v2/assign', {
        ticket_id: ticketId,
        assigned_to: lawyerId
      });
      toast.success(i18n.language === 'ar' ? 'تم إسناد التذكرة' : 'Ticket assigned');
      fetchTicketDetails();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل الإسناد' : 'Failed to assign');
    }
  };

  const handleReopenTicket = async () => {
    setUpdatingStatus(true);
    try {
      await api.put(`/tickets-v2/${ticketId}`, { status: 'reopened' });
      toast.success(i18n.language === 'ar' ? 'تم إعادة فتح التذكرة' : 'Ticket reopened');
      fetchTicketDetails();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إعادة الفتح' : 'Failed to reopen');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!ticket) return null;

  const isStaff = user.role === 'admin' || user.role === 'lawyer';
  const canReopen = ticket.status === 'closed' && user.role === 'client';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/tickets')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              {i18n.language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{ticket.ticket_number}</h1>
                {getStatusBadge(ticket.status)}
                {getPriorityBadge(ticket.priority)}
              </div>
              <h2 className="text-xl text-gray-700">{ticket.title}</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Ticket Details */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'تفاصيل التذكرة' : 'Ticket Details'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="prose max-w-none">
                    <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'العميل' : 'Client'}</p>
                      <p className="font-semibold flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {ticket.client_name}
                      </p>
                    </div>
                    {ticket.case_number && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'القضية' : 'Case'}</p>
                        <p className="font-semibold flex items-center gap-2">
                          <Briefcase className="w-4 h-4" />
                          {ticket.case_number}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'تاريخ الإنشاء' : 'Created At'}</p>
                      <p className="font-semibold flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm')}
                      </p>
                    </div>
                    {ticket.assigned_to_name && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{i18n.language === 'ar' ? 'مسند إلى' : 'Assigned To'}</p>
                        <p className="font-semibold flex items-center gap-2 text-blue-600">
                          <User className="w-4 h-4" />
                          {ticket.assigned_to_name}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Tabs for Replies and Timeline */}
              <Card className="shadow-lg">
                <Tabs defaultValue="replies" className="w-full">
                  <CardHeader>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="replies">
                        {i18n.language === 'ar' ? 'الردود' : 'Replies'} ({replies.length})
                      </TabsTrigger>
                      <TabsTrigger value="timeline">
                        {i18n.language === 'ar' ? 'السجل الزمني' : 'Timeline'} ({timeline.length})
                      </TabsTrigger>
                    </TabsList>
                  </CardHeader>
                  
                  <CardContent>
                    <TabsContent value="replies" className="space-y-4 mt-0">
                      {replies.length === 0 ? (
                        <div className="text-center py-12">
                          <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">{i18n.language === 'ar' ? 'لا توجد ردود بعد' : 'No replies yet'}</p>
                        </div>
                      ) : (
                        replies.map((reply) => (
                          <div 
                            key={reply.id} 
                            className={`p-4 rounded-xl border-l-4 ${
                              reply.is_internal_note 
                                ? 'bg-yellow-50 border-l-yellow-500' 
                                : reply.user_role === 'client' 
                                  ? 'bg-blue-50 border-l-blue-500' 
                                  : 'bg-green-50 border-l-green-500'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  reply.is_internal_note ? 'bg-yellow-600' : reply.user_role === 'client' ? 'bg-blue-600' : 'bg-green-600'
                                }`}>
                                  <User className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                  <p className="font-bold text-gray-900">{reply.user_name}</p>
                                  <p className="text-xs text-gray-600">
                                    {formatDistanceToNow(new Date(reply.created_at), { 
                                      addSuffix: true, 
                                      locale: i18n.language === 'ar' ? ar : undefined 
                                    })}
                                  </p>
                                </div>
                              </div>
                              {reply.is_internal_note && (
                                <Badge className="bg-yellow-600 text-white gap-1">
                                  <Eye className="w-3 h-3" />
                                  {i18n.language === 'ar' ? 'داخلية' : 'Internal'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-gray-700 whitespace-pre-wrap">{reply.message}</p>
                          </div>
                        ))
                      )}
                    </TabsContent>
                    
                    <TabsContent value="timeline" className="space-y-3 mt-0">
                      {timeline.map((event) => {
                        const Icon = event.icon;
                        return (
                          <div key={event.id} className="flex gap-3 pb-3 border-b last:border-b-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 flex-shrink-0`}>
                              <Icon className={`w-5 h-5 ${event.color}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-1">
                                <h5 className="font-bold text-gray-900">{event.title}</h5>
                                <span className="text-xs text-gray-500">
                                  {format(new Date(event.timestamp), 'dd/MM HH:mm')}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{event.description}</p>
                              <p className="text-xs text-gray-500 mt-1">{event.user}</p>
                            </div>
                          </div>
                        );
                      })}
                    </TabsContent>
                  </CardContent>
                </Tabs>
              </Card>

              {/* Reply Form */}
              {ticket.status !== 'closed' && (
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle>{i18n.language === 'ar' ? 'إضافة رد' : 'Add Reply'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSendReply} className="space-y-4">
                      <Textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder={i18n.language === 'ar' ? 'اكتب ردك هنا...' : 'Write your reply here...'}
                        rows={4}
                        className="resize-none"
                      />
                      
                      {isStaff && (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2 space-x-reverse">
                            <Checkbox 
                              id="internal-note" 
                              checked={isInternalNote}
                              onCheckedChange={setIsInternalNote}
                            />
                            <Label htmlFor="internal-note" className="cursor-pointer flex items-center gap-2">
                              <Eye className="w-4 h-4 text-yellow-600" />
                              {i18n.language === 'ar' ? 'ملاحظة داخلية (غير مرئية للعميل)' : 'Internal Note (not visible to client)'}
                            </Label>
                          </div>
                          
                          <div className="flex items-center space-x-2 space-x-reverse">
                            <Checkbox 
                              id="add-timeline" 
                              checked={addToTimeline}
                              onCheckedChange={setAddToTimeline}
                            />
                            <Label htmlFor="add-timeline" className="cursor-pointer flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-600" />
                              {i18n.language === 'ar' ? 'إضافة إلى السجل الزمني' : 'Add to Timeline'}
                            </Label>
                          </div>
                        </div>
                      )}
                      
                      <Button type="submit" className="btn-primary gap-2" disabled={sendingReply}>
                        {sendingReply ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            {i18n.language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            {i18n.language === 'ar' ? 'إرسال الرد' : 'Send Reply'}
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Reopen Button for Clients */}
              {canReopen && (
                <Card className="shadow-lg border-2 border-blue-200 bg-blue-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <RotateCcw className="w-6 h-6 text-blue-600" />
                        <div>
                          <h4 className="font-bold text-gray-900">
                            {i18n.language === 'ar' ? 'إعادة فتح التذكرة' : 'Reopen Ticket'}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {i18n.language === 'ar' ? 'إذا لم يتم حل المشكلة، يمكنك إعادة فتح التذكرة' : 'If the issue is not resolved, you can reopen the ticket'}
                          </p>
                        </div>
                      </div>
                      <Button 
                        onClick={handleReopenTicket} 
                        disabled={updatingStatus}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {i18n.language === 'ar' ? 'إعادة فتح' : 'Reopen'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* SLA Timer */}
              {getSLATimer()}

              {/* Admin Controls */}
              {isStaff && (
                <>
                  {/* Status Change */}
                  <Card className="shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-lg">{i18n.language === 'ar' ? 'تغيير الحالة' : 'Change Status'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Select 
                        value={ticket.status} 
                        onValueChange={handleStatusChange}
                        disabled={updatingStatus}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">{i18n.language === 'ar' ? 'جديدة' : 'New'}</SelectItem>
                          <SelectItem value="in_progress">{i18n.language === 'ar' ? 'قيد المعالجة' : 'In Progress'}</SelectItem>
                          <SelectItem value="replied">{i18n.language === 'ar' ? 'تم الرد' : 'Replied'}</SelectItem>
                          <SelectItem value="closed">{i18n.language === 'ar' ? 'مغلقة' : 'Closed'}</SelectItem>
                          <SelectItem value="reopened">{i18n.language === 'ar' ? 'معاد فتحها' : 'Reopened'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>

                  {/* Assign Ticket */}
                  <Card className="shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-lg">{i18n.language === 'ar' ? 'إسناد التذكرة' : 'Assign Ticket'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Select 
                        value={ticket.assigned_to || ''} 
                        onValueChange={handleAssignTicket}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={i18n.language === 'ar' ? 'اختر محامي...' : 'Select lawyer...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {lawyers.map((lawyer) => (
                            <SelectItem key={lawyer.id} value={lawyer.id}>
                              {lawyer.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Ticket Info */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{i18n.language === 'ar' ? 'معلومات إضافية' : 'Additional Info'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">{i18n.language === 'ar' ? 'رقم التذكرة' : 'Ticket Number'}</span>
                    <span className="font-semibold">{ticket.ticket_number}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">{i18n.language === 'ar' ? 'النوع' : 'Type'}</span>
                    <Badge variant="outline">{ticket.ticket_type}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-gray-600">{i18n.language === 'ar' ? 'الأولوية' : 'Priority'}</span>
                    {getPriorityBadge(ticket.priority)}
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-600">{i18n.language === 'ar' ? 'آخر تحديث' : 'Last Updated'}</span>
                    <span className="text-sm font-semibold">
                      {formatDistanceToNow(new Date(ticket.updated_at), { 
                        addSuffix: true,
                        locale: i18n.language === 'ar' ? ar : undefined 
                      })}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TicketDetailsPage;
